import type { ComponentProps, FormEvent, HTMLAttributes, KeyboardEventHandler } from 'react';

import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { cn } from '../../lib';
import { CornerDownLeftIcon, SquareIcon } from 'lucide-react';
import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';

// Simplified V1 prompt input following AI Elements API patterns.
// For V2, replace with full `npx ai-elements@latest add prompt-input`.

type PromptInputStatus = 'ready' | 'submitted' | 'streaming' | 'error';

export type PromptInputMessage = {
  text: string;
};

type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  onSubmit?: (message: PromptInputMessage, event: FormEvent) => void;
};

export const PromptInput = ({ onSubmit, className, children, ...props }: PromptInputProps) => {
  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const text = (formData.get('prompt') as string)?.trim();
      if (text) {
        onSubmit?.({ text }, e);
      }
    },
    [onSubmit],
  );

  return (
    <form
      className={cn(
        'flex flex-col gap-0 rounded-xl border bg-background shadow-sm focus-within:ring-1 focus-within:ring-ring',
        className,
      )}
      onSubmit={handleSubmit}
      {...props}
    >
      {children}
    </form>
  );
};

type PromptInputTextareaProps = Omit<ComponentProps<'textarea'>, 'onKeyDown'> & {
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
};

export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  ({ className, onKeyDown, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) ?? internalRef;

    const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
      e => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const form = e.currentTarget.closest('form');
          form?.requestSubmit();
        }
        onKeyDown?.(e);
      },
      [onKeyDown],
    );

    const adjustHeight = useCallback(() => {
      const el = textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
      }
    }, [textareaRef]);

    // Auto-resize on mount when textarea has pre-filled content
    useLayoutEffect(() => {
      adjustHeight();
    }, [adjustHeight]);

    return (
      <textarea
        ref={textareaRef}
        name="prompt"
        rows={1}
        className={cn(
          'w-full resize-none border-0 bg-transparent px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        onKeyDown={handleKeyDown}
        onInput={adjustHeight}
        {...props}
      />
    );
  },
);

PromptInputTextarea.displayName = 'PromptInputTextarea';

type PromptInputFooterProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputFooter = ({ className, ...props }: PromptInputFooterProps) => (
  <div className={cn('flex items-center justify-between px-3 py-2', className)} {...props} />
);

type PromptInputSubmitProps = ComponentProps<typeof Button> & {
  status?: PromptInputStatus;
};

export const PromptInputSubmit = ({
  status = 'ready',
  className,
  children,
  ...props
}: PromptInputSubmitProps) => {
  const isLoading = status === 'submitted' || status === 'streaming';

  const icon =
    children ??
    (isLoading ? (
      status === 'streaming' ? (
        <SquareIcon className="size-4 fill-current" />
      ) : (
        <Spinner className="size-4" />
      )
    ) : (
      <CornerDownLeftIcon className="size-4" />
    ));

  return (
    <Button
      className={cn('ml-auto shrink-0', className)}
      size="icon"
      type="submit"
      variant={isLoading ? 'destructive' : 'default'}
      {...props}
    >
      {icon}
    </Button>
  );
};
